import moment from 'moment';
import pluralize from 'pluralize';
import React from 'react';

import {
  DISTRICT_PREMIUM,
  getSubscriptionTheme,
  SALESMATE_RENEWAL_FORM_URL,
  SCHOOL_PREMIUM,
  SCHOOL_PREMIUM_SCHOLARSHIP,
  TEACHER_PREMIUM,
  TEACHER_PREMIUM_CREDIT,
  TEACHER_PREMIUM_SCHOLARSHIP,
  TEACHER_PREMIUM_TRIAL
} from './constants';

import StripeSubscriptionCheckoutSessionButton from '../shared/StripeSubscriptionCheckoutSessionButton';

const CTA_BUTTON_CLASSNAME = "quill-button medium contained green focus-on-light"

const quillBasicCopy = (
  <span>
    Quill Basic provides access to all of Quill&apos;s content. To access Quill Premium, you can purchase an individual teacher subscription or a school subscription.
  </span>);

function schoolPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact) {
  if (remainingDays < 0) { return expiredCopy(subscriptionType, subscriptionStatus, userIsContact) }

  return (
    <span>
      With {subscriptionType}, you will have access to all of Quill’s
      free reports as well as additional advanced reporting. You will also
      be able to view and print reports of your students’ progress. Our
      advanced reports support concept, Common Core, and overall progress
      analysis. <a className="green-link focus-on-light" href="https://support.quill.org/quill-premium">Here’s more information</a> about your {subscriptionType} features.
    </span>
  )
};

function teacherPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact) {
  if (remainingDays < 0) { return expiredCopy(subscriptionType, subscriptionStatus) }

  return (
    <span>
      With {subscriptionType}, you will have access to all of Quill’s free reports as well as additional advanced reporting.
      You will also be able to view and print reports of your students’ progress. Our advanced reports support concept, Common Core, and overall progress analysis.
      <a className="green-link focus-on-light" href="https://support.quill.org/quill-premium"> Here’s more information</a> about your {subscriptionType} features.
    </span>
  )
};

function expiredCopy(subscriptionType, subscriptionStatus, userIsContact) {
  const { purchaser_email, purchaser_name, } = subscriptionStatus

  const expiration = moment(subscriptionStatus.expiration)
  const dateFormat = "MM/DD/YY"
  const formattedStartDate = subscriptionStatus && moment(subscriptionStatus.start_date).format(dateFormat)
  const formattedExpirationDate = expiration && expiration.format(dateFormat)

  let billingContact

  if (!userIsContact && purchaser_email) {
    const billingContactEmailLink = <a href={`mailto:${purchaser_email}`}>{purchaser_email}</a>
    const billingContactContent = purchaser_name ? <React.Fragment>{purchaser_name} ({billingContactEmailLink})</React.Fragment> : billingContactEmailLink
    billingContact = <React.Fragment><br /><br />The billing contact for your subscription is <strong>{billingContactContent}.</strong></React.Fragment>
  }

  return (
    <span>
      <strong>Your {subscriptionType} subscription ({formattedStartDate} - {formattedExpirationDate}) has expired and you are back to Quill Basic.</strong>
      {quillBasicCopy}
      {billingContact}
    </span>
  )
}

const SubscriptionStatus = ({
  subscriptionStatus,
  subscriptionType,
  userIsContact,
  customerEmail,
}) => {

  const renewalStripePriceId = subscriptionStatus && subscriptionStatus.renewal_stripe_price_id
  const schoolIds = subscriptionStatus && JSON.stringify(subscriptionStatus.school_ids)

  let expiration
  let remainingDays

  let subscriptionTypeText = subscriptionType;

  if (subscriptionType !== 'Basic') {
    expiration = moment(subscriptionStatus.expiration);
    remainingDays = expiration.diff(moment(Date.now()), 'days');
  }

  const content = {
    status: (
      <h2>{subscriptionTypeText}</h2>
    ),
    buttonOrDate: expiration && (
      <span className="expiration-date">
        <span>Valid Until:</span> <span>{`${expiration.format('MMMM Do, YYYY')}`}</span><span className="time-left-in-days"> | {`${remainingDays} ${pluralize('days', remainingDays)}`}</span>
      </span>
    )
  };

  switch (subscriptionType) {
    case 'Basic':
      content.premiumCopy = quillBasicCopy;
      content.buttonOrDate = <a className="quill-button contained green outlined focus-on-light" href="/premium">Learn more about Quill Premium</a>;
      subscriptionTypeText = 'Quill Basic';
      content.status = <h2>{subscriptionTypeText}</h2>;
      break;
    case TEACHER_PREMIUM_TRIAL:
    case TEACHER_PREMIUM_CREDIT:
    case TEACHER_PREMIUM_SCHOLARSHIP:
      content.premiumCopy = teacherPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact);
      const teacherSubDisplayName = subscriptionType === TEACHER_PREMIUM_SCHOLARSHIP ? TEACHER_PREMIUM : subscriptionType
      content.status = <h2>{teacherSubDisplayName}</h2>
      if (remainingDays < 0) {
        content.buttonOrDate = <a className={CTA_BUTTON_CLASSNAME} href="/premium">Subscribe to premium</a>
      }
      break;
    case TEACHER_PREMIUM:
      content.premiumCopy = teacherPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact);
      content.status = <h2>{subscriptionType}</h2>
      if (remainingDays < 0) {
        content.buttonOrDate = (
          <StripeSubscriptionCheckoutSessionButton
            buttonClassName={CTA_BUTTON_CLASSNAME}
            buttonText='Renew subscription'
            cancelPath='subscriptions'
            customerEmail={subscriptionStatus.customer_email || customerEmail}
            stripePriceId={renewalStripePriceId}
            userIsEligibleForNewSubscription={true}
            userIsSignedIn={true}
          />
        )
      }
      break;
    case SCHOOL_PREMIUM:
      content.premiumCopy = schoolPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact);
      content.status = <h2>{subscriptionType}</h2>;
      if (remainingDays < 0) {
        content.buttonOrDate = (
          <StripeSubscriptionCheckoutSessionButton
            buttonClassName={CTA_BUTTON_CLASSNAME}
            buttonText='Renew subscription'
            cancelPath='subscriptions'
            customerEmail={customerEmail}
            schoolIds={schoolIds}
            stripePriceId={renewalStripePriceId}
            userIsEligibleForNewSubscription={true}
            userIsSignedIn={true}
          />
        )
      }
      break;
    case DISTRICT_PREMIUM:
      content.premiumCopy = schoolPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact);
      content.status = <h2>{subscriptionType}</h2>;
      if (remainingDays < 0) {
        content.buttonOrDate = (
          <a
            className={CTA_BUTTON_CLASSNAME}
            href={SALESMATE_RENEWAL_FORM_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            Contact us to renew
          </a>
        )
      }
      break;
    case SCHOOL_PREMIUM_SCHOLARSHIP:
      content.premiumCopy = schoolPremiumCopy(subscriptionType, subscriptionStatus, remainingDays, userIsContact);
      content.status = <h2>{SCHOOL_PREMIUM}</h2>;
      if (remainingDays < 0) {
        content.buttonOrDate = <a className={CTA_BUTTON_CLASSNAME} href="/premium">Subscribe to premium</a>
      }
      break;
  }

  if (remainingDays < 0) {
    content.status = <h2>{`Your ${subscriptionType} subscription has expired`}</h2>
  }

  // Fall back to the neutral Basic theme when the user has no active premium
  // (expired or never subscribed) so the maroon / gold tints don't bleed into
  // the "Your X has expired" empty-state copy.
  const hasActivePremium = subscriptionStatus && !subscriptionStatus.expired
  const themeClass = hasActivePremium ? getSubscriptionTheme(subscriptionType) : 'basic-theme'
  const assetsBase = window.postbuildEnv.CDN_URL || 'https://assets.quill.org'
  const quillLogoSrc = `${assetsBase}/images/logos/quill-logo-green.svg`
  const premiumDiamondSrc = `${assetsBase}/images/icons/l/premium.svg`

  const validUntilRow = expiration && remainingDays >= 0 && (
    <div className="valid-until-row">
      <span className="valid-until-label">Valid Until:</span>
      <span className="valid-until-date">{expiration.format('MMMM Do, YYYY')}</span>
      <span className="days-remaining">{`${remainingDays} ${pluralize('day', remainingDays)}`}</span>
    </div>
  )

  // content.buttonOrDate carries the "Valid Until" span by default; we render
  // that ourselves above. Only surface buttonOrDate when it's actually a button
  // (Basic / expired flows).
  const cta = (expiration && remainingDays >= 0) ? null : content.buttonOrDate

  return (
    <section aria-labelledby="subscriptionStatusTitle" className={`subscription-status ${themeClass}`}>
      <div className="upper-section">
        <img alt="Quill" className="quill-mark" src={quillLogoSrc} />
        <div className="plan-name-container" id="subscriptionStatusTitle">
          {content.status}
        </div>
      </div>
      <div className="lower-section">
        {validUntilRow}
        <p className="premium-subscription-copy">{content.premiumCopy}</p>
        {cta && <div className="cta-row">{cta}</div>}
        {hasActivePremium && (
          <img alt="" aria-hidden="true" className="premium-diamond" src={premiumDiamondSrc} />
        )}
      </div>
    </section>
  );
}

export default SubscriptionStatus
